import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect, useState
} from "react"
import { Button, Card, Form } from "react-bootstrap"

import { LineSeriesOption } from "echarts"
import {
  PrevetSpeedGraphZoneType,
  getZonesOfConcernWithSinglePointZoneIndex,
} from "../getSpeedChartOptionsHelpers"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { useFetchVesselLegs } from "../hooks/useFetchVesselLegs"
import { PrevetSpeedGraphModal } from "./PrevetSpeedGraphModal"
import { MemoPrevetVesselSpeedChart } from "./PrevetVesselSpeedChart"

interface PrevetVesselSpeedProps {
  vesselImo: number
  vesselName: string
  setIsTradingModalShown: React.Dispatch<React.SetStateAction<boolean>>
  setDateForCenterMarker: React.Dispatch<
    React.SetStateAction<undefined | string>
  >
  setLadenAvgSpeed: Dispatch<React.SetStateAction<undefined | number>>
  setBallastAvgSpeed: Dispatch<React.SetStateAction<undefined | number>>
  setLadenMaxSpeed: Dispatch<React.SetStateAction<undefined | number>>
  setBallastMaxSpeed: Dispatch<React.SetStateAction<undefined | number>>
  ladenChartInstance: MutableRefObject<any>
  ballastChartInstance: MutableRefObject<any>
  userLadenVesselSpeed: string | undefined
  userBallastVesselSpeed: string | undefined
  endDate: string
  setPrevetSpeedGraphZoneType: Dispatch<
    SetStateAction<PrevetSpeedGraphZoneType | undefined>
  >
  prevetSpeedGraphZoneType: PrevetSpeedGraphZoneType | undefined
  setVesselSpeedDateRange: Dispatch<SetStateAction<string>>
  vesselSpeedDateRange: string
  // setPerformanceExposure:  React.Dispatch<React.SetStateAction<number | undefined>>
}

export function PrevetVesselSpeedWrapper({
  vesselImo,
  vesselName,
  setIsTradingModalShown,
  setDateForCenterMarker,
  setLadenAvgSpeed,
  setBallastAvgSpeed,
  setLadenMaxSpeed,
  setBallastMaxSpeed,
  ladenChartInstance,
  ballastChartInstance,
  userLadenVesselSpeed,
  userBallastVesselSpeed,
  endDate,
  setPrevetSpeedGraphZoneType,
  prevetSpeedGraphZoneType,
  setVesselSpeedDateRange,
  vesselSpeedDateRange,
  // setPerformanceExposure
}: PrevetVesselSpeedProps) {
  /* radio button for 6 months and 1 year */
  // const [selectedOption, setSelectedOption] = useState<string>(DEFAULT_SPEED_DATE_RANGE)
  const { setTabStateValue } = usePrevetTabStateContext()
  const { vesselLegsData } = useFetchVesselLegs({
    imo: vesselImo,
    end: endDate,
    start: vesselSpeedDateRange,
  })
  const [isSpeedGraphModalShown, setIsSpeedGraphModalShown] = useState(false)

  // Imperatively set the ocean zones on chart instead of passing zone type
  // To the memoized chart component as that will cause chart to rerender
  // and remove the user inputted TC Desc.
  useEffect(() => {
    setTabStateValue(
      "vesselSpeedGraphZoneType",
      prevetSpeedGraphZoneType,
      undefined
    )
  }, [prevetSpeedGraphZoneType])

  function handleChangeZoneType(id: string | undefined) {
    setPrevetSpeedGraphZoneType(id as PrevetSpeedGraphZoneType)
    if (!vesselLegsData || vesselLegsData.length < 1) {
      return
    }
    const ladenOpts = ladenChartInstance.current.getOption()
    const ballastOpts = ballastChartInstance.current.getOption()
    const {
      zonesOfConcern: ladenZones,
      indexOfSinglePointZones: ladenIndexOfSinglePointZones,
    } = getZonesOfConcernWithSinglePointZoneIndex({
      speedData: vesselLegsData.filter((data) => data.laden === true),
      isLaden: true,
      useForMinTreshold: id as PrevetSpeedGraphZoneType,
    })
    const {
      zonesOfConcern: ballastZones,
      indexOfSinglePointZones: ballastIndexOfSinglePointZones,
    } = getZonesOfConcernWithSinglePointZoneIndex({
      speedData: vesselLegsData.filter((data) => data.laden === false),
      isLaden: false,
      useForMinTreshold: id as PrevetSpeedGraphZoneType,
    })

    ladenChartInstance.current.setOption({
      ...ladenOpts,
      series: [
        // Order of ladenOpts items in series array has to match initial order
        {
          ...(ladenOpts.series as LineSeriesOption[])[0],
          markArea: {
            ...(ladenOpts.series as LineSeriesOption[])[0].markArea,
            data: ladenZones,
          },
        },
        {
          ...(ladenOpts.series as LineSeriesOption[])[1],
        },
        {
          type: "line",
          xAxisIndex: 1,
          markArea: {
            // Enables the hack to draw a mark area around a single point
            ...(ladenOpts.series as LineSeriesOption[])[2].markArea,
            data: ladenIndexOfSinglePointZones.map((idx) => {
              return [{ xAxis: idx * 2 }, { xAxis: idx * 2 + 1 }]
            }),
          },
        },
      ],
    })
    ballastChartInstance.current.setOption({
      ...ballastOpts,
      series: [
        // Order of ladenOpts items in series array has to match initial order
        {
          ...(ballastOpts.series as LineSeriesOption[])[0],
          markArea: {
            ...(ballastOpts.series as LineSeriesOption[])[0].markArea,
            data: ballastZones,
          },
        },
        {
          ...(ballastOpts.series as LineSeriesOption[])[1],
        },
        {
          type: "line",
          xAxisIndex: 1,
          markArea: {
            // Enables the hack to draw a mark area around a single point
            ...(ballastOpts.series as LineSeriesOption[])[2].markArea,
            data: ballastIndexOfSinglePointZones.map((idx) => {
              return [{ xAxis: idx * 2 }, { xAxis: idx * 2 + 1 }]
            }),
          },
        },
      ],
    })
  }

  useEffect(() => {
    handleChangeZoneType(prevetSpeedGraphZoneType)
  }, [prevetSpeedGraphZoneType])

  const handleOptionChange = (value: string) => {
    setTabStateValue("vesselSpeedDateRange", value, undefined)
    setVesselSpeedDateRange(value)
  }

  //calculating global minmax

  const [globalMin, setGlobalMin] = useState<number>(5) // Default min
  const [globalMax, setGlobalMax] = useState<number>(14) // Default max

  useEffect(() => {
    if (vesselLegsData) {
      const ladenSpeeds = vesselLegsData
        .filter((data) => data.laden)
        .map((item) => item.sog)
      const ballastSpeeds = vesselLegsData
        .filter((data) => !data.laden)
        .map((item) => item.sog)

      const allSpeeds = [...ladenSpeeds, ...ballastSpeeds]
      const minSpeed = Math.min(...allSpeeds)
      const maxSpeed = Math.max(...allSpeeds)

      // Adjust the global min and max dynamically
      setGlobalMin(Math.min(minSpeed, 5)) // Use data value if lower than 5, else default to 5
      setGlobalMax(Math.max(maxSpeed, 14)) // Use data value if higher than 14, else default to 14
    }
  }, [vesselLegsData])

  return (
    <Card.Body className="PrevetTrio_SpeedGraph_Inner">
      {isSpeedGraphModalShown && (
        <PrevetSpeedGraphModal
          isSpeedGraphModalShown={isSpeedGraphModalShown}
          vesselImo={vesselImo}
          vesselName={vesselName}
          closeSpeedGraphModal={() => {
            setIsSpeedGraphModalShown(false)
          }}
          userLadenVesselSpeed={userLadenVesselSpeed}
          userBallastVesselSpeed={userBallastVesselSpeed}
          setIsTradingModalShown={setIsTradingModalShown}
          setDateForCenterMarker={setDateForCenterMarker}
          endDate={endDate}
          startDate={vesselSpeedDateRange}
          globalMin={globalMin}
          globalMax={globalMax}
        />
      )}
      <div className="mb-0">
        <div className="mb-0 ">
          <div className="d-flex mb-4"
            style={{ width: "calc(100% - 10px)", display:"flex", marginBottom: "1rem", justifyContent: "space-between", alignItems: "center" }} >
            <h4 className="card-title mb-0 me-2"
              style={{ alignContent: "center", width: "90px", whiteSpace: "nowrap" }}>Vessel Speed</h4>
            <div className="d-flex align-items-center justify-content-space-between"
              style={{ width: "90vw", flexWrap: "wrap", paddingLeft: "5px" }} >
              <Form className="d-flex flex-fill align-items-start justify-content-start" >
                <div style={{ display: "flex", width: "85px", alignItems: "center" }}>
                  <Form.Check
                    type="radio"
                    id="sixMonths"
                    name="duration"
                    value="6 Months"
                    checked={vesselSpeedDateRange === "6 Months"}
                    onChange={(event) => handleOptionChange(event.target.value)} />
                  <Form.Label style={{ whiteSpace: "nowrap", marginBottom: "0" }}>6 Months</Form.Label>
                </div>
                <div style={{ display: "flex", width: "85px", alignItems: "center" }}>
                  <Form.Check
                    type="radio"
                    id="oneYear"
                    name="duration"
                    value="1 Year"
                    checked={vesselSpeedDateRange === "1 Year"}
                    onChange={(event) => handleOptionChange(event.target.value)}
                  />
                  <Form.Label style={{ whiteSpace: "nowrap", marginBottom: "0" }}>1 Year</Form.Label>
                </div>
              </Form>
              <Form className="d-flex">
                {[
                  /*  PrevetSpeedGraphZoneType.Dips, */
                  PrevetSpeedGraphZoneType.Outliers,
                  PrevetSpeedGraphZoneType.None,
                ].map((zoneType) => {
                  return (
                    <div style={{ display: "flex", width: "85px", alignItems: "center" }}>
                      <Form.Check
                        className="d-flex align-items-start"
                        type="checkbox"
                        id={zoneType}
                        key={zoneType} >
                        <Form.Check.Input
                          style={{ float: "revert" }}
                          type="checkbox"
                          checked={zoneType === prevetSpeedGraphZoneType}
                          onChange={(event) =>
                            handleChangeZoneType(event.target.id)
                          }
                        />
                        <Form.Check.Label
                          style={{ width: "unset" }}
                          className="m-0 ms-1 mt-0" >
                          {zoneType}
                        </Form.Check.Label>
                      </Form.Check>
                    </div>
                  )
                })}
              </Form>
            </div>
            <Button
              className="PrevetTrio_SpeedGraph_OceanZoneButton_VesselSpeed"
              onClick={() => setIsSpeedGraphModalShown(true)}
            >
              Ocean Zones
            </Button>
          </div>
          <div className="d-flex flex-column gap-4 w-100">
            <MemoPrevetVesselSpeedChart
              vesselImo={vesselImo}
              vesselName={vesselName}
              isLaden={true}
              setAvgSpeed={setLadenAvgSpeed}
              setMaxSpeed={setLadenMaxSpeed}
              setIsTradingModalShown={setIsTradingModalShown}
              setDateForCenterMarker={setDateForCenterMarker}
              chartInstance={ladenChartInstance}
              useForMinTreshold={PrevetSpeedGraphZoneType.Outliers}
              endDate={endDate}
              startDate={vesselSpeedDateRange}
              globalMin={globalMin}
              globalMax={globalMax}
            />
            <MemoPrevetVesselSpeedChart
              vesselImo={vesselImo}
              vesselName={vesselName}
              isLaden={false}
              setAvgSpeed={setBallastAvgSpeed}
              setMaxSpeed={setBallastMaxSpeed}
              setIsTradingModalShown={setIsTradingModalShown}
              setDateForCenterMarker={setDateForCenterMarker}
              chartInstance={ballastChartInstance}
              useForMinTreshold={PrevetSpeedGraphZoneType.Outliers}
              endDate={endDate}
              startDate={vesselSpeedDateRange}
              globalMin={globalMin}
              globalMax={globalMax}
            />
          </div>
        </div>
      </div>
    </Card.Body>
  )
}
